// Non-employee commission workspace: a paying organization manages commission
// payees (agents, contractors, delivery/owner-operator partners), records
// qualifying activities, calculates commission with country withholding, and
// runs the approve → pay lifecycle — SEPARATE from employee payroll. Reuses the
// KaziPeople design system; new only where the settlement domain requires it.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Download, Info, FileText, ShieldCheck } from 'lucide-react';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { commissionApi, type CommissionLine, type CommissionPayee, type TaxPreview, type TreatyClaim } from '../api/commissionApi';
import { getApiErrorMessage } from '@/lib/apiError';
import { PayeesPanel } from '../components/PayeesPanel';
import { RunsPanel } from '../components/RunsPanel';
import { TreatyClaimsPanel } from '../components/TreatyClaimsPanel';

const STATUS_VARIANT: Record<string, 'secondary' | 'warning' | 'info' | 'success' | 'danger'> = {
  calculated: 'info',
  under_review: 'warning',
  approved: 'warning',
  payment_ready: 'warning',
  submitted: 'info',
  paid: 'success',
  rejected: 'danger',
  on_hold: 'secondary',
};

const WORKER_CATEGORIES = [
  'COMMISSION_AGENT', 'INDEPENDENT_CONTRACTOR', 'DELIVERY_PARTNER', 'OWNER_OPERATOR',
  'REFERRAL_PARTNER', 'CONSULTANT', 'FREELANCER', 'VENDOR', 'CASUAL_WORKER', 'OTHER_NON_EMPLOYEE',
];

function money(v: string, ccy: string) {
  const n = Number(v || '0');
  return `${ccy} ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function Row({ k, v, bold, muted }: { k: string; v: string; bold?: boolean; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`tabular-nums ${bold ? 'font-semibold text-foreground' : muted ? 'text-xs text-muted-foreground' : 'text-foreground'}`}>{v}</dd>
    </div>
  );
}

export function CommissionPage() {
  const { canApprovePayroll } = useRoleAccess();
  const [lines, setLines] = useState<CommissionLine[] | null>(null);
  // ME7 Part C: fee amounts are compensation. Without pay-details access the
  // line list is refused (403) — say so, never show it as "no commissions"
  // or as zero totals. Payees, runs and treaty claims still load.
  const [linesRestricted, setLinesRestricted] = useState(false);
  // No contractor access at all (payees refused too), e.g. a plain employee.
  const [noAccess, setNoAccess] = useState(false);
  const [payees, setPayees] = useState<CommissionPayee[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [treatyLine, setTreatyLine] = useState<CommissionLine | null>(null);

  const refresh = async () => {
    const [l, p] = await Promise.allSettled([commissionApi.listLines(), commissionApi.listPayees()]);
    if (p.status === 'fulfilled') setPayees(p.value);
    if (l.status === 'fulfilled') {
      setLinesRestricted(false);
      setLines(l.value);
    } else if ((l.reason as any)?.response?.status === 403) {
      setLinesRestricted(true);
      setLines([]);
    } else {
      setLinesRestricted(false);
      setLines([]);
    }
    setNoAccess(l.status === 'rejected' && p.status === 'rejected'
      && (l.reason as any)?.response?.status === 403 && (p.reason as any)?.response?.status === 403);
    const failed = [l, p].find((r): r is PromiseRejectedResult => r.status === 'rejected' && (r.reason as any)?.response?.status !== 403);
    if (failed) toast.error(getApiErrorMessage(failed.reason, 'Could not load commissions'));
  };
  useEffect(() => { refresh(); }, []);

  const totals = useMemo(() => {
    const t = { gross: 0, wht: 0, net: 0, count: 0, ccy: 'TZS' };
    (lines ?? []).forEach((l) => {
      t.gross += Number(l.gross_commission || 0);
      t.wht += Number(l.wht_amount || 0);
      t.net += Number(l.net_amount || 0);
      t.count += 1;
      t.ccy = l.currency || t.ccy;
    });
    return t;
  }, [lines]);

  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(id);
    try {
      await fn();
      toast.success(ok);
      await refresh();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Action failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6 p-1">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Contractor &amp; External Pay</h1>
          <p className="text-sm text-muted-foreground">
            Pay non-employees (agents, contractors, delivery and owner-operator partners) for completed work. Separate from payroll; withholding applied per country rules.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Record commission</Button>
      </div>

      {noAccess && (
        <Card data-testid="contractor-no-access">
          <CardContent className="flex items-start gap-3 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">You don&apos;t have access to Contractor &amp; External Pay</p>
              <p className="text-sm text-muted-foreground">
                Contractors and their fees are visible to payroll and finance staff of the paying company. Your own contractor statements are under My Pay.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {linesRestricted && !noAccess && (
        <Card data-testid="fees-restricted">
          <CardContent className="flex items-start gap-3 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">Fee amounts are hidden for you</p>
              <p className="text-sm text-muted-foreground">
                Contractor fees are confidential pay, like salaries. Seeing them needs payroll details access for the company that pays the contractor. You can still see the contractors themselves.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!linesRestricted && <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          ['Gross commission', money(String(totals.gross), totals.ccy)],
          ['Withholding', money(String(totals.wht), totals.ccy)],
          ['Net payable', money(String(totals.net), totals.ccy)],
          ['Commission lines', String(totals.count)],
        ].map(([label, value]) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>}

      <PayeesPanel payees={payees} />

      <RunsPanel approvedLines={lines ?? []} canApprove={canApprovePayroll} onChanged={refresh} />

      <TreatyClaimsPanel payees={payees} canApprove={canApprovePayroll} />

      {!linesRestricted && <Card>
        <CardContent className="p-0">
          {lines === null ? (
            <div className="space-y-2 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : lines.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center">
              <Info className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium text-foreground">No commissions yet</p>
              <p className="text-sm text-muted-foreground">Record a commission to calculate earnings and pay a non-employee.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payee</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Withholding</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.line_id}>
                    <TableCell className="font-medium">{l.payee_name || l.payee_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-muted-foreground">{l.plan_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(l.gross_commission, l.currency)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{money(l.wht_amount, l.currency)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{money(l.net_amount, l.currency)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[l.status] ?? 'secondary'}>{l.status.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {l.status === 'calculated' && canApprovePayroll && (
                          <Button size="sm" variant="outline" disabled={busy === l.line_id}
                            onClick={() => act(l.line_id, () => commissionApi.approve(l.line_id), 'Approved')}>Approve</Button>
                        )}
                        {l.status === 'approved' && canApprovePayroll && (
                          <Button size="sm" disabled={busy === l.line_id}
                            onClick={() => act(l.line_id, () => commissionApi.payout(l.line_id), 'Sent to PayGate')}>Pay</Button>
                        )}
                        <Button size="sm" variant="ghost" title="Statement"
                          onClick={() => commissionApi.downloadStatement(l.line_id, `commission-${l.line_id.slice(0, 8)}.pdf`).catch((e) => toast.error(getApiErrorMessage(e, 'Download failed')))}>
                          <Download className="h-4 w-4" />
                        </Button>
                        {Number(l.wht_amount) > 0 && (
                          <Button size="sm" variant="ghost" title="WHT certificate"
                            onClick={() => commissionApi.downloadCertificate(l.line_id, `wht-certificate-${l.line_id.slice(0, 8)}.pdf`).catch((e) => toast.error(getApiErrorMessage(e, 'Download failed')))}>
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                        {l.treaty_allowed && !l.treaty_claim_id && (l.status === 'calculated' || l.status === 'under_review') && canApprovePayroll && (
                          <Button size="sm" variant="ghost" title="Apply treaty relief" onClick={() => setTreatyLine(l)}>
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>}

      <RecordCommissionDialog open={open} onClose={() => setOpen(false)} payees={payees} onDone={refresh} />
      <TreatyApplyDialog line={treatyLine} onClose={() => setTreatyLine(null)} onDone={refresh} />
    </div>
  );
}

function TreatyApplyDialog({ line, onClose, onDone }: {
  line: CommissionLine | null; onClose: () => void; onDone: () => Promise<void>;
}) {
  const [claims, setClaims] = useState<TreatyClaim[] | null>(null);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (!line) { setClaims(null); return; }
    commissionApi.listTreatyClaims(line.payee_id)
      .then((cs) => setClaims(cs.filter((c) => c.status === 'approved')))
      .catch(() => setClaims([]));
  }, [line]);

  const apply = async (claimId: string) => {
    if (!line) return;
    setBusy(claimId);
    try { await commissionApi.applyTreatyClaim(line.line_id, claimId); toast.success('Treaty relief applied'); onClose(); await onDone(); }
    catch (e: any) { toast.error(getApiErrorMessage(e, 'Could not apply the claim')); }
    finally { setBusy(''); }
  };

  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Apply tax-treaty relief</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Pick an approved treaty claim that is valid on this payment's tax-point date. The claim's rate replaces the default withholding.</p>
        <div className="space-y-1.5">
          {claims === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : claims.length === 0 ? (
            <p className="text-sm text-warning">No approved treaty claims for this payee. Create and approve one in the Treaty relief claims panel first.</p>
          ) : claims.map((c) => (
            <button key={c.id} disabled={!!busy}
              className="flex w-full items-center justify-between rounded-md border border-border p-2.5 text-left text-sm hover:bg-muted/40 disabled:opacity-50"
              onClick={() => apply(c.id)}>
              <span>{c.treaty_country} · art.{c.treaty_article} · {(Number(c.approved_rate) * 100).toFixed(2)}%</span>
              <span className="text-xs text-muted-foreground">valid {c.valid_from}→{c.valid_to}</span>
            </button>
          ))}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordCommissionDialog({ open, onClose, payees, onDone }: {
  open: boolean; onClose: () => void; payees: CommissionPayee[]; onDone: () => Promise<void>;
}) {
  const [payeeId, setPayeeId] = useState('');
  const [newPayee, setNewPayee] = useState(false);
  const [np, setNp] = useState({ legal_name: '', worker_category: 'COMMISSION_AGENT', country_code: 'TZ', entity_type: 'individual', residency: 'RESIDENT', tax_identifier: '', treaty_eligible: false, treaty_country: '', payout_provider: 'MPESA', payout_account: '', agreement_signed: true });
  const [plan, setPlan] = useState({ name: '', plan_type: 'percentage', currency: 'TZS', percent_rate: '0.10', flat_amount: '0' });
  const [activity, setActivity] = useState({ source: 'sale', external_ref: '', event_date: '', customer: '', gross_amount: '' });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<TaxPreview | null>(null);
  const [previewErr, setPreviewErr] = useState('');

  // Live tax preview: recompute (debounced) whenever the payment inputs change.
  const selectedExisting = payees.find((p) => p.payee_id === payeeId);
  const previewCountry = newPayee ? np.country_code : (selectedExisting?.country_code || 'TZ');
  const previewCategory = newPayee ? np.worker_category : (selectedExisting?.worker_category || '');
  useEffect(() => {
    const gross = activity.gross_amount;
    if (!gross || Number(gross) <= 0 || !previewCategory) { setPreview(null); setPreviewErr(''); return; }
    const t = setTimeout(async () => {
      try {
        const p = await commissionApi.preview({
          country_code: previewCountry, worker_category: previewCategory,
          entity_type: newPayee ? np.entity_type : undefined,
          residency: newPayee ? np.residency : undefined,
          plan_type: plan.plan_type, percent_rate: plan.percent_rate, flat_amount: plan.flat_amount,
          eligible_amount: gross, currency: plan.currency, event_date: activity.event_date || undefined,
        });
        setPreview(p); setPreviewErr('');
      } catch (e: any) {
        setPreview(null);
        setPreviewErr(e?.response?.data?.message || 'Tax rule could not be resolved for this payment');
      }
    }, 350);
    return () => clearTimeout(t);
  }, [previewCountry, previewCategory, newPayee, np.entity_type, np.residency, plan.plan_type, plan.percent_rate, plan.flat_amount, plan.currency, activity.gross_amount, activity.event_date]);

  const submit = async () => {
    setSaving(true);
    try {
      let pid = payeeId;
      if (newPayee) {
        if (!np.legal_name || !np.payout_account) { toast.error('Payee name and payout account required'); setSaving(false); return; }
        pid = (await commissionApi.createPayee({ ...np, payout_channel: np.payout_provider === 'BANK' ? 'bank' : 'mobile_money' })).payee_id;
      }
      if (!pid) { toast.error('Choose or add a payee'); setSaving(false); return; }
      if (!plan.name || !activity.external_ref || !activity.gross_amount) { toast.error('Plan name, activity reference and amount are required'); setSaving(false); return; }
      const planRes = await commissionApi.createPlan({
        name: plan.name, plan_type: plan.plan_type, country_code: np.country_code, currency: plan.currency,
        percent_rate: plan.plan_type === 'percentage' ? plan.percent_rate : '0',
        flat_amount: plan.plan_type === 'flat' ? plan.flat_amount : '0',
        effective_from: '2024-01-01',
      });
      const actRes = await commissionApi.recordActivity({
        payee_id: pid, plan_id: planRes.plan_id, source: activity.source, external_ref: activity.external_ref,
        event_date: activity.event_date || undefined, customer: activity.customer,
        gross_amount: activity.gross_amount, currency: plan.currency,
      });
      await commissionApi.calculate(actRes.activity_id);
      toast.success('Commission calculated');
      onClose();
      await onDone();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not record commission'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Record commission</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Payee</Label>
            {!newPayee ? (
              <div className="flex gap-2">
                <Select value={payeeId} onValueChange={setPayeeId}>
                  <SelectTrigger><SelectValue placeholder="Select a payee" /></SelectTrigger>
                  <SelectContent>
                    {payees.map((p) => <SelectItem key={p.payee_id} value={p.payee_id}>{p.legal_name} · {p.worker_category.replace(/_/g, ' ').toLowerCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" type="button" onClick={() => setNewPayee(true)}>New</Button>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border border-border p-3">
                <Input placeholder="Legal name" value={np.legal_name} onChange={(e) => setNp({ ...np, legal_name: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={np.worker_category} onValueChange={(v) => setNp({ ...np, worker_category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{WORKER_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ').toLowerCase()}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={np.payout_provider} onValueChange={(v) => setNp({ ...np, payout_provider: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['MPESA', 'AIRTEL', 'TIGO', 'SELCOM', 'NMB', 'BANK'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={np.residency} onValueChange={(v) => setNp({ ...np, residency: v })}>
                    <SelectTrigger><SelectValue placeholder="Tax residency" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RESIDENT">Resident</SelectItem>
                      <SelectItem value="NON_RESIDENT">Non-resident</SelectItem>
                      <SelectItem value="UNKNOWN">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={np.entity_type} onValueChange={(v) => setNp({ ...np, entity_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Recipient type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="business">Company / business</SelectItem>
                      <SelectItem value="sole_proprietor">Sole proprietor</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input placeholder="Tax identification number (TIN)" value={np.tax_identifier} onChange={(e) => setNp({ ...np, tax_identifier: e.target.value })} />
                <Input placeholder="Payout account / wallet number" value={np.payout_account} onChange={(e) => setNp({ ...np, payout_account: e.target.value })} />
                {np.residency === 'UNKNOWN' && (
                  <p className="text-xs text-warning">Residency is required to withhold correctly — the payment will be blocked until it's resolved.</p>
                )}
                <Button variant="ghost" size="sm" type="button" onClick={() => setNewPayee(false)}>Use an existing payee</Button>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Commission plan</Label>
            <Input placeholder="Plan name (e.g. Agent 10%)" value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Select value={plan.plan_type} onValueChange={(v) => setPlan({ ...plan, plan_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="flat">Flat per activity</SelectItem></SelectContent>
              </Select>
              {plan.plan_type === 'percentage' ? (
                <Input placeholder="Rate e.g. 0.10" value={plan.percent_rate} onChange={(e) => setPlan({ ...plan, percent_rate: e.target.value })} />
              ) : (
                <Input placeholder="Flat amount" value={plan.flat_amount} onChange={(e) => setPlan({ ...plan, flat_amount: e.target.value })} />
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Qualifying activity</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Reference (e.g. SALE-001)" value={activity.external_ref} onChange={(e) => setActivity({ ...activity, external_ref: e.target.value })} />
              <Input placeholder="Gross amount" value={activity.gross_amount} onChange={(e) => setActivity({ ...activity, gross_amount: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Customer (optional)" value={activity.customer} onChange={(e) => setActivity({ ...activity, customer: e.target.value })} />
              <Input type="date" value={activity.event_date} onChange={(e) => setActivity({ ...activity, event_date: e.target.value })} />
            </div>
          </div>
          {(preview || previewErr) && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <div className="mb-1 font-medium text-foreground">Tax preview</div>
              {previewErr ? (
                <p className="text-warning">{previewErr}</p>
              ) : preview && preview.treatment === 'EMPLOYMENT_INCOME' ? (
                <p className="text-muted-foreground">Employment income — this should be added to gross taxable earnings and taxed via PAYE on the employee payroll run.</p>
              ) : preview ? (
                <dl className="space-y-1">
                  <Row k="Gross amount" v={money(preview.gross_commission, preview.currency)} />
                  <Row k="Tax treatment" v={`${preview.residency === 'NON_RESIDENT' ? 'Non-resident' : 'Resident'} withholding${preview.finality === 'FINAL' ? ' (final)' : ''}`} />
                  <Row k="Applicable rate" v={`${(Number(preview.wht_rate) * 100).toFixed(2)}%`} />
                  <Row k="Withholding tax" v={money(preview.wht_amount, preview.currency)} />
                  <Row k="Net payable" v={money(preview.net_amount, preview.currency)} bold />
                  <Row k="Rule" v={preview.rule_code} muted />
                  {preview.treaty_allowed && <p className="pt-1 text-xs text-muted-foreground">A tax-treaty override may apply — available after calculation.</p>}
                </dl>
              ) : null}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Calculating…' : 'Record & calculate'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CommissionPage;
